import type { TechlineCaseEntity } from '@packages/domain/techline/cases';

import { getLogger } from '@packages/core/logging';
import { colors } from '@packages/core/styles';
import { Button, Icon, Label, Typography } from '@packages/ui/shared';
import InfoCard from '@packages/ui/shared/info-card';
// FIXME: accountsService is imported only to pass to InfoCard (TECHSHARE-5977). Once InfoCard is refactored to be
//        presentational (receives contact data as props), this import and the accountService prop can be removed.
//        - Luke C, 03/09/2026, TECHSHARE-5977
import { accountsService } from '@web/services/singletons';
import { useCallback, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { StyleSheet, View } from 'react-native';
import AsyncSelect from 'react-select/async';

import { type AccountSearchOption, useAccountSearch } from '../../../hooks/use-account-search';

const logger = getLogger('techline');

/**
 * Props for the CaseTeamSection component.
 * Displays the list of team members on a case and provides
 * a modal to search for and add new members.
 */
export type CaseTeamSectionProps = {
    /** Whether the add-member button should be disabled. */
    isAddMemberDisabled?: boolean;

    /** Whether to hide the add-member button entirely. */
    isAddMemberButtonHidden?: boolean;

    /** Callback to add a team member by username. Returns true on success. */
    onAddTeamMember: (username: string) => Promise<boolean>;

    /** Callback to display an error message to the user. */
    onError?: (error: string) => void;

    /** The retailer number scoping account searches. */
    retailerNumber: string;

    /** The list of retailer profiles (team members) currently on the case. */
    retailerProfiles: TechlineCaseEntity['retailerProfiles'];
};

/** Style for the raised icon container on the add-member button. */
const addMemberIconContainerStyle = { width: 22, height: 22 };

/** Focus state passed to react-select style functions. */
type SelectFocusState = {
    /** Whether the element is currently focused. */
    isFocused: boolean;
};

/**
 * Display case team members and provide an add-member modal.
 * Renders the current retailer profiles with info cards, and
 * (optionally) a button that opens a search modal to add new
 * team members to the case.
 *
 * This component is presentational: it delegates team member
 * addition to the `onAddTeamMember` callback and uses the
 * `useAccountSearch` hook for the async member search.
 *
 * @param props - component props
 * @returns the case team section with member list and add-member modal
 */
export function CaseTeamSection({
    isAddMemberButtonHidden = false,
    isAddMemberDisabled,
    onAddTeamMember,
    onError,
    retailerNumber,
    retailerProfiles,
}: CaseTeamSectionProps) {
    // State
    const [isAddingMember, setIsAddingMember] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedOption, setSelectedOption] = useState<AccountSearchOption | null>(null);

    // Hooks
    // Exception: useAccountSearch stays here rather than being lifted to the composition layer because
    // it is a stateless, read-only lookup hook (not case state); lifting it would just thread two props
    // through the parent for no architectural benefit.
    const { fetchAccountsAsSelectOptions, isSearching } = useAccountSearch(retailerNumber);

    // Handlers

    /**
     * Open the add-member modal.
     */
    const handleAddMemberButtonPress = useCallback(() => {
        setIsModalVisible(true);
    }, []);

    /**
     * Close the add-member modal.
     */
    const handleModalHide = useCallback(() => {
        setIsModalVisible(false);
    }, []);

    /**
     * Update the selected option when the user picks from the dropdown.
     *
     * @param option - the selected account search option
     */
    const handleMemberSelectChange = useCallback((option: AccountSearchOption | null) => {
        setSelectedOption(option);
    }, []);

    /**
     * Handle the continue button click in the add-member modal.
     * Calls onAddTeamMember with the selected account's username,
     * shows an error on failure, and closes the modal when done.
     */
    const handleContinueButtonClick = useCallback(async () => {
        if (!selectedOption) {
            return;
        }
        try {
            setIsAddingMember(true);
            const success = await onAddTeamMember(selectedOption.value.soaUsername);
            if (!success) {
                onError?.('User could not be added because of a server error.');
            }
        } catch (error) {
            logger.error('Failed to add team member', { error });
            onError?.('User could not be added because of a server error.');
        } finally {
            setIsAddingMember(false);
            setIsModalVisible(false);
        }
    }, [onAddTeamMember, onError, selectedOption]);

    // JSX
    return (
        <>
            <div className="d-flex flex-column mt-3">
                <Typography color="black" variant="h5">
                    Case team
                </Typography>
            </div>

            {retailerProfiles.map((profile, index) => {
                return (
                    <View key={index}>
                        <View style={styles.avatarWrapper}>
                            <View style={styles.avatarNameWrapper}>
                                <InfoCard
                                    accountService={accountsService}
                                    content={`${profile.userName} (${profile.soaUserId})`}
                                    userName={profile.soaUserId}
                                />
                                <Typography variant="caption">{profile.createdOn}</Typography>
                            </View>
                        </View>
                    </View>
                );
            })}
            {!isAddMemberButtonHidden && (
                <div className="d-flex mt-5">
                    <Button
                        disabled={isAddMemberDisabled}
                        iconLeft={
                            <Icon name="plus" raised raisedContainerStyle={addMemberIconContainerStyle} size={16} />
                        }
                        onPress={handleAddMemberButtonPress}
                    >
                        Add member
                    </Button>
                </div>
            )}
            <Modal centered={true} onHide={handleModalHide} show={isModalVisible} size="lg">
                <Modal.Header>
                    <Modal.Title>Add member to case</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <Label required>Member name</Label>
                    <AsyncSelect
                        isLoading={isSearching}
                        loadOptions={fetchAccountsAsSelectOptions}
                        onChange={handleMemberSelectChange}
                        placeholder={''}
                        styles={asyncSelectStyles}
                    />
                </Modal.Body>

                <Modal.Footer>
                    <div className="mr-2">
                        <Button onPress={handleModalHide} variant="ghost-blue">
                            Cancel
                        </Button>
                    </div>

                    <Button
                        disabled={isAddingMember || !selectedOption}
                        isLoading={isAddingMember}
                        onPress={handleContinueButtonClick}
                        variant="primary"
                    >
                        Continue
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}

/**
 * Custom styles for the AsyncSelect member search dropdown.
 * Configures the control padding, hides the indicator separator,
 * sets the menu portal z-index, and styles focused/unfocused options.
 */
const asyncSelectStyles = {
    /**
     * Overrides the control padding and applies a blue box-shadow border.
     *
     * @param base - the default styles from react-select
     * @param _state - the component state containing focus info (unused; required by react-select API)
     * @returns the merged style object
     */
    control: (base: Record<string, unknown>, _state: SelectFocusState) => ({
        ...base,
        boxShadow: `0 0 0 1px ${colors.blueOne}`,
        padding: 6,
    }),
    /**
     * Hides the default indicator separator line.
     *
     * @param base - the default styles from react-select
     * @returns the merged style object with zero width
     */
    indicatorSeparator: (base: Record<string, unknown>) => ({
        ...base,
        width: 0,
    }),
    /**
     * Raises the menu portal z-index so it renders above modals.
     *
     * @param base - the default styles from react-select
     * @returns the merged style object with elevated z-index
     */
    menuPortal: (base: Record<string, unknown>) => ({ ...base, zIndex: 99999999 }),
    /**
     * Styles focused and unfocused dropdown options with brand colors.
     *
     * @param base - the default styles from react-select
     * @param state - the component state containing focus info
     * @returns the merged style object with brand color theming
     */
    option: (base: Record<string, unknown>, state: SelectFocusState) => ({
        ...base,
        backgroundColor: state.isFocused ? colors.blueOne : 'white',
        color: state.isFocused ? colors.white : colors.black,
        lineHeight: '18px',
        padding: 20,
    }),
};

const styles = StyleSheet.create({
    avatarWrapper: {
        flexDirection: 'row',
        alignItems: 'center',
        marginTop: 8,
    },
    avatarNameWrapper: {
        marginLeft: 16,
    },
});
